/**
 * Supplier Search Polling Middleware
 * Manages polling logic for supplier searches
 */

import { setGlobalSuccessToastMessage, setGlobalErrorToastMessage } from '@UI/GlobalToast';
import type { Middleware, MiddlewareAPI, Dispatch } from '@reduxjs/toolkit';
import type { RootState } from '../types';
import type { AppStore } from '../store';
import {
  startSupplierSearch,
  checkSearchStatus,
  incrementPollingAttempts,
  stopPolling,
  startPolling,
  selectAllActiveSearches,
} from '../slices/supplierSearchSlice';

// Store active polling intervals
const pollingIntervals = new Map<number, NodeJS.Timeout>();

// Middleware
export const supplierSearchPollingMiddleware: Middleware<Record<string, never>, RootState> = (store) => (next) => (action) => {
  const result = next(action);

  // When a search starts successfully, begin polling
  if (startSupplierSearch.fulfilled.match(action)) {
    const { productId, task } = action.payload;
    startPollingForProduct(store, productId, task.id);
  }

  // When polling is stopped manually, clear the interval
  if (stopPolling.match(action)) {
    const { productId } = action.payload;
    stopPollingForProduct(productId);
  }

  // When search status is checked and complete/failed, stop polling and show alert
  if (checkSearchStatus.fulfilled.match(action)) {
    const { productId, task } = action.payload;

    if (task.status === 'completed') {
      stopPollingForProduct(productId);

      // Show success alert
      if (typeof window !== 'undefined') {
          setGlobalSuccessToastMessage(
            'supplierHub.search.complete',
          );
      }
    } else if (task.status === 'failed') {
      stopPollingForProduct(productId);

      // Show error toast with translation key
      // TODO: Replace with translation key when GlobalToast supports i18n
      if (typeof window !== 'undefined') {
          setGlobalErrorToastMessage(
            'supplierHub.search.failed',
          );
      }
    }
  }

  return result;
};

// Helper function to start polling for a product
function startPollingForProduct(store: MiddlewareAPI<Dispatch, RootState>, productId: number, taskId: string) {
  // Clear any existing interval for this product
  stopPollingForProduct(productId);

  const pollingInterval = setInterval(() => {
    const state: RootState = store.getState();
    const polling = state.supplierSearch.polling[productId.toString()];

    // Check if polling should continue
    if (!polling || !polling.active) {
      stopPollingForProduct(productId);
      return;
    }

    // Check if max attempts reached
    if (polling.attempts >= polling.maxAttempts) {
      console.warn(`Max polling attempts reached for product ${productId}`);
      store.dispatch(stopPolling({ productId }));
      stopPollingForProduct(productId);

      if (typeof window !== 'undefined') {
        console.log(`Supplier search timed out for product ${productId}. Please try again later.`);
      }
      return;
    }

    // Increment attempts and check status
    store.dispatch(incrementPollingAttempts({ productId }));
    // @ts-expect-error - Middleware dispatch type limitation
    store.dispatch(checkSearchStatus({ taskId, productId }));
  }, 15000); // 15 seconds

  pollingIntervals.set(productId, pollingInterval);
}

// Helper function to stop polling for a product
function stopPollingForProduct(productId: number) {
  const interval = pollingIntervals.get(productId);
  if (interval) {
    clearInterval(interval);
    pollingIntervals.delete(productId);
  }
}

// Initialize polling for active searches on app load
export function initializePollingOnLoad(store: AppStore) {
  if (typeof window === 'undefined') return;

  const state = store.getState() as unknown as RootState;
  const activeSearches = selectAllActiveSearches(state);

  activeSearches.forEach(({ productId, task }) => {
    console.log(`Resuming polling for product ${productId}, task ${task.id}`);

    // First, activate polling in state
    store.dispatch(startPolling({ productId }));

    // Then start the polling interval
    startPollingForProduct(store as unknown as MiddlewareAPI<Dispatch, RootState>, productId, task.id);
  });
}

